/* src/ui/hud/interiorHud.ts — the chrome for the walkable interior (v2).
 *
 * The walk mode is render/interiorView.ts's; this is its dashboard and, now, its shop counter.
 * It shows where you are, a floor button per storey (locked until stairs reach it), an owner's
 * build palette priced from the live market, the "E — stock shelf" aim prompt, and the way out.
 *
 * It still knows nothing about geometry: state arrives on `interior:state` / `interior:aim`,
 * and every button press leaves as an event or a callback the composition root wired in. The
 * palette's prices are the ticker's — display only; the server walks the real curve when the
 * purchase happens (CLAUDE.md #2).
 */

import { emit, on, type InteriorState } from '../../core/events.ts';
import { gameStore } from '../../core/store.ts';
import { Component } from '../component.ts';
import { el, text } from '../dom.ts';
import { formatMoney } from '../format.ts';

/** What the palette can install, and which market row prices it. */
const PALETTE: ReadonlyArray<{ kind: string; label: string; item: string | null; blurb: string }> = [
  { kind: 'stairs', label: 'Stairs', item: null, blurb: 'Reach the next floor' },
  { kind: 'shelf', label: 'Shelf', item: 'SHOP_SHELF', blurb: 'Sells one item' },
  { kind: 'checkout', label: 'Checkout', item: 'SHOP_REGISTER', blurb: 'Customers pay here' },
  { kind: 'storage', label: 'Storage', item: 'STOCKROOM_RACK', blurb: '+200 stock space' },
];

export interface InteriorHudHooks {
  /** Buy a fixture on the current floor. The HUD disables the button while this runs. */
  onBuy: (parcelId: string, kind: string, floor: number) => Promise<void>;
  /** Open the stock/manage panel for an aimed fixture. */
  onInteract: (parcelId: string, fixtureId: string, kind: string) => void;
}

export class InteriorHud extends Component<InteriorHudHooks> {
  readonly #name = text('');
  readonly #floorLabel = text('');
  readonly #status = text('');
  readonly #aimLabel = text('');
  #root!: HTMLElement;
  #floorRow!: HTMLElement;
  #palette!: HTMLElement;
  #aim!: HTMLElement;
  readonly #buttons = new Map<number, HTMLButtonElement>();
  readonly #paletteButtons = new Map<string, { button: HTMLButtonElement; price: Text }>();
  #state: InteriorState = { active: false };
  #aimTarget: { fixtureId: string; kind: string } | null = null;
  #busy = false;
  /** Signature of the last-built floor row, so it only rebuilds when counts change. */
  #builtKey = '';

  protected render(): HTMLElement {
    this.#floorRow = el('div', { class: 'gf-interior-floors', attrs: { 'aria-label': 'Floors' } });

    this.#palette = el('div', { class: 'gf-interior-palette' });
    for (const p of PALETTE) {
      const price = text('');
      const button = el(
        'button',
        {
          class: 'gf-interior-buy',
          attrs: { type: 'button', title: p.blurb },
          on: { click: () => void this.#buy(p.kind) },
        },
        el('span', { class: 'gf-interior-buylabel' }, p.label),
        el('span', { class: 'gf-interior-buyprice' }, price),
      );
      this.#paletteButtons.set(p.kind, { button, price });
      this.#palette.appendChild(button);
    }

    this.#aim = el('div', { class: 'gf-interior-aim', attrs: { hidden: true } },
      el('span', { class: 'gf-interior-aimkey' }, 'E'),
      el('span', null, this.#aimLabel));

    this.#root = el(
      'div',
      { class: 'gf-interior', attrs: { hidden: true } },
      el(
        'div',
        { class: 'gf-interior-head' },
        el('span', { class: 'gf-interior-kind' }, 'YOUR BUILDING'),
        el('span', { class: 'gf-interior-name' }, this.#name),
        el('span', { class: 'gf-interior-shopstat' }, this.#status),
      ),
      this.#floorRow,
      this.#palette,
      el(
        'div',
        { class: 'gf-interior-foot' },
        el('span', { class: 'gf-interior-floor' }, this.#floorLabel),
        el('span', { class: 'gf-interior-hint' },
          'Click to look · WASD walk · walk the stairs · E use · Esc leave'),
        el(
          'button',
          {
            class: 'gf-interior-leave',
            attrs: { type: 'button' },
            on: { click: () => emit('interior:exit') },
          },
          'LEAVE',
        ),
      ),
      this.#aim,
    );
    return this.#root;
  }

  protected override onMount(): void {
    this.onCleanup(on('interior:state', (state) => this.#apply(state)));
    this.onCleanup(
      on('interior:aim', (target) => {
        this.#aimTarget = target;
        this.#paintAim();
      }),
    );
    this.onCleanup(
      on('interior:interact', ({ fixtureId, kind }) => {
        if (this.#state.active) this.props.onInteract(this.#state.parcelId, fixtureId, kind);
      }),
    );
    // Market prices move once a minute; keep the palette honest.
    this.bind(
      (s) => s?.market ?? null,
      () => this.#paintPalette(),
    );
    this.bind(
      (s) => s?.land ?? null,
      () => this.#paintStatus(),
    );
  }

  async #buy(kind: string): Promise<void> {
    if (!this.#state.active || this.#busy) return;
    this.#busy = true;
    this.#paintPalette();
    try {
      await this.props.onBuy(this.#state.parcelId, kind, this.#state.floor);
    } finally {
      this.#busy = false;
      this.#paintPalette();
    }
  }

  #apply(state: InteriorState): void {
    this.#state = state;
    if (state.active) {
      document.documentElement.dataset['gfInterior'] = 'active';
    } else {
      delete document.documentElement.dataset['gfInterior'];
    }

    if (!state.active) {
      this.#root.hidden = true;
      return;
    }

    this.#root.hidden = false;
    this.#name.textContent = state.name;
    this.#floorLabel.textContent = `FLOOR ${String(state.floor + 1)} / ${String(state.floors)}`;

    const key = `${String(state.floors)}:${String(state.reachableFloors)}`;
    if (this.#builtKey !== key) {
      this.#builtKey = key;
      this.#buttons.clear();
      const buttons: HTMLButtonElement[] = [];
      for (let f = 0; f < state.floors; f += 1) {
        const locked = f >= state.reachableFloors;
        const button = el(
          'button',
          {
            class: `gf-interior-fbtn${locked ? ' is-locked' : ''}`,
            attrs: {
              type: 'button',
              title: locked ? `Floor ${String(f + 1)} — build stairs to reach it` : `Floor ${String(f + 1)}`,
              ...(locked ? { disabled: true } : {}),
            },
            data: { floor: String(f) },
            on: { click: () => emit('interior:setfloor', { floor: f }) },
          },
          locked ? '🔒' : String(f + 1),
        );
        this.#buttons.set(f, button);
        buttons.push(button);
      }
      buttons.reverse();
      this.#floorRow.replaceChildren(...buttons);
    }
    for (const [f, button] of this.#buttons) {
      button.classList.toggle('is-active', f === state.floor);
    }

    this.#paintPalette();
    this.#paintStatus();
    this.#paintAim();
  }

  /** Palette prices come off the ticker; stairs are priced by the building (from the poll). */
  #paintPalette(): void {
    if (!this.#state.active) return;
    const market = gameStore.get()?.market ?? [];
    for (const p of PALETTE) {
      const entry = this.#paletteButtons.get(p.kind);
      if (!entry) continue;
      if (p.item) {
        const row = market.find((m) => m.key === p.item);
        entry.price.textContent = row ? `~${formatMoney(row.price)}` : '—';
      } else {
        entry.price.textContent = 'per floor';
      }
      const stairsDone =
        p.kind === 'stairs' &&
        (this.#state.floor >= this.#state.floors - 1 ||
          this.#state.floor < this.#state.reachableFloors - 1);
      entry.button.disabled = this.#busy || stairsDone;
      entry.button.classList.toggle('is-disabled', this.#busy || stairsDone);
    }
  }

  /** The one-line verdict: trading and at what rate, or the next thing this shop needs. */
  #paintStatus(): void {
    if (!this.#state.active) return;
    const holding = gameStore
      .get()
      ?.land.holdings.find((h) => h.parcelId === (this.#state.active ? this.#state.parcelId : ''));
    if (!holding) {
      this.#status.textContent = '';
      return;
    }
    this.#status.textContent = holding.shopOpen
      ? `OPEN · ${formatMoney(holding.shopPerMin)}/min`
      : holding.fixtures === 0
        ? 'EMPTY — fit it out below'
        : 'CLOSED — needs a stocked shelf and a checkout';
  }

  #paintAim(): void {
    if (!this.#state.active || !this.#aimTarget) {
      this.#aim.hidden = true;
      return;
    }
    const verb =
      this.#aimTarget.kind === 'shelf' ? 'Stock this shelf'
      : this.#aimTarget.kind === 'checkout' ? 'Checkout'
      : 'Storage';
    this.#aimLabel.textContent = verb;
    this.#aim.hidden = false;
  }
}
